'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { formatDate, getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import ArticleProgressBar from '@/components/ArticleProgressBar'
import AdSlotWrapper from '@/components/AdSlotWrapper'
import MarketRatesStrip from '@/components/market/MarketRatesStrip'
import {
  mapArticleTagsForMarket,
  resolveInstrumentsFromTags,
  type ArticleMarketTag,
} from '@/lib/market-rates'

interface News {
  id: number
  title: string
  slug: string
  image: string | null
  createdAt: Date | string
  category: {
    id: number
    title: string
    slug: string
  } | null
}

interface CurrentNews {
  id: number
  title: string
  slug: string
  image: string | null
  category: {
    id: number
    title: string
    slug: string
  } | null
}

interface PrevNextArticles {
  prev: News | null
  next: News | null
}

interface NewsSidebarProps {
  currentNewsId: number
  currentNews: CurrentNews
  currentNewsImageUrl?: string | null
  categoryId: number
  categorySlug: string
  articleTags?: ArticleMarketTag[]
}

function tagsFromNews(news: { tags?: unknown } | null | undefined): ArticleMarketTag[] {
  if (!news || !Array.isArray((news as { tags?: unknown }).tags)) return []
  return mapArticleTagsForMarket((news as { tags: Parameters<typeof mapArticleTagsForMarket>[0] }).tags)
}

/**
 * NewsSidebar - Shows Previous and Next articles
 * Fetches prev/next articles from API endpoint
 */
export default function NewsSidebar({
  currentNewsId,
  currentNews,
  currentNewsImageUrl,
  categoryId,
  categorySlug,
  articleTags = [],
}: NewsSidebarProps) {
  const [prevNext, setPrevNext] = useState<PrevNextArticles>({ prev: null, next: null })
  const [loading, setLoading] = useState(true) // Start with true to show skeleton on initial load
  const [activeNews, setActiveNews] = useState<CurrentNews>(currentNews)
  const [activeNewsId, setActiveNewsId] = useState<number>(currentNewsId)
  const [activeTags, setActiveTags] = useState<ArticleMarketTag[]>(articleTags)
  const articleTagsRef = useRef(articleTags)
  const lastNewsIdRef = useRef<number>(-1)
  const activeNewsIdRef = useRef<number>(currentNewsId)
  const currentNewsRef = useRef<HTMLDivElement>(null)
  const sidebarContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    articleTagsRef.current = articleTags
    if (activeNewsIdRef.current === currentNewsId) {
      setActiveTags(articleTags)
    }
  }, [articleTags, currentNewsId])

  const marketInstruments = resolveInstrumentsFromTags(activeTags)

  // Helper function to format news for sidebar
  const formatNewsForSidebar = (news: any): News | null => {
    if (!news) return null
    return {
      id: news.id,
      title: news.title || '',
      slug: news.slug || '',
      image: news.image,
      createdAt: news.createdAt || new Date(),
      category: news.category
    }
  }

  // Update prev/next from event data - no API calls needed
  // All data comes from NextNewsInfiniteScroll which already has the news list
  const updateFromEvent = useCallback((news: any, prevNews: any, nextNews: any) => {
    if (!news || !news.id) return
    
    lastNewsIdRef.current = news.id
    
    setActiveNews({
      id: news.id,
      title: news.title,
      slug: news.slug,
      image: news.image,
      category: news.category,
    })
    setActiveNewsId(news.id)
    activeNewsIdRef.current = news.id
    setActiveTags(
      news.id === currentNewsId ? articleTagsRef.current : tagsFromNews(news),
    )
    
    setPrevNext({
      prev: formatNewsForSidebar(prevNews),
      next: formatNewsForSidebar(nextNews)
    })
    setLoading(false)
  }, [currentNewsId])

  // Update activeNews when currentNews prop changes (initial load)
  useEffect(() => {
    if (currentNews && currentNews.id === currentNewsId) {
      setActiveNews(currentNews)
      setActiveNewsId(currentNewsId)
      activeNewsIdRef.current = currentNewsId
    }
  }, [currentNews, currentNewsId])

  // Don't fetch prev/next on initial load - wait for NextNewsInfiniteScroll event
  // Initial fetch removed to prevent showing wrong next news from API

  // Scroll to active news when it changes
  useEffect(() => {
    if (!activeNewsId) return
    
    const scrollToActiveNews = () => {
      if (currentNewsRef.current && sidebarContainerRef.current) {
        const sidebarRect = sidebarContainerRef.current.getBoundingClientRect()
        const currentNewsRect = currentNewsRef.current.getBoundingClientRect()
        
        // Check if current news is not fully visible
        const sidebarTop = sidebarRect.top
        const sidebarBottom = sidebarRect.bottom
        const currentNewsTop = currentNewsRect.top
        const currentNewsBottom = currentNewsRect.bottom
        
        // If current news is above visible area or partially below
        if (currentNewsTop < sidebarTop + 20 || currentNewsBottom > sidebarBottom - 20) {
          const scrollOffset = currentNewsTop - sidebarTop - 100 // 100px offset from top
          
          sidebarContainerRef.current.scrollTo({
            top: sidebarContainerRef.current.scrollTop + scrollOffset,
            behavior: 'smooth'
          })
        }
      }
    }
    
    // Delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToActiveNews, 200)
    
    return () => clearTimeout(timeoutId)
  }, [activeNewsId])

  // Listen for news changes from NextNewsInfiniteScroll - no API calls needed
  useEffect(() => {
    const handleNewsChanged = (e: Event) => {
      const customEvent = e as CustomEvent
      const eventData = customEvent.detail
      
      const news = eventData.current || eventData
      const prevNews = eventData.prev || null
      const nextNews = eventData.next || null
      
      if (news && news.id) {
        // Special handling for first news
        if (news.id === currentNewsId) {
          setActiveNews(currentNews)
          setActiveNewsId(currentNewsId)
          activeNewsIdRef.current = currentNewsId
          lastNewsIdRef.current = currentNewsId
          setActiveTags(articleTagsRef.current)
          setPrevNext({
            prev: null,
            next: formatNewsForSidebar(nextNews)
          })
          setLoading(false)
          return
        }
        
        if (news.id !== activeNewsIdRef.current) {
          updateFromEvent(news, prevNews, nextNews)
        }
      }
    }

    const handleNextNewsPrefetched = (e: Event) => {
      const customEvent = e as CustomEvent
      const nextNews = customEvent.detail
      if (nextNews && nextNews.id) {
        setPrevNext(prev => ({
          prev: prev.prev,
          next: formatNewsForSidebar(nextNews)
        }))
      }
    }
    
    window.addEventListener('newsChanged', handleNewsChanged as EventListener)
    window.addEventListener('nextNewsPrefetched', handleNextNewsPrefetched as EventListener)

    return () => {
      window.removeEventListener('newsChanged', handleNewsChanged as EventListener)
      window.removeEventListener('nextNewsPrefetched', handleNextNewsPrefetched as EventListener)
    }
  }, [currentNewsId, currentNews, updateFromEvent])

  return (
    <aside className="sidebar-container w-full shrink-0 lg:w-72 lg:max-w-[18rem] xl:w-80 xl:max-w-[20rem] lg:sticky lg:top-24 lg:self-start">
      <div
        ref={sidebarContainerRef}
        className="news-sidebar-inner max-h-[calc(100vh-8rem)] overflow-y-auto rounded-lg border border-gray-200 shadow-sm dark:border-slate-600"
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Previous Article */}
        {loading ? (
          <div className="border-b border-gray-200 p-2.5 sm:p-3 dark:border-slate-700" aria-busy="true">
            <div className="mb-2 h-3 w-16 rounded bg-gray-200 dark:bg-slate-700" />
            <div className="flex gap-2">
              <div className="sidebar-thumb shrink-0 rounded bg-gray-200 dark:bg-slate-700" />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-slate-700" />
                <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-slate-700" />
              </div>
            </div>
          </div>
        ) : prevNext.prev ? (
          <Link
            href={`/${prevNext.prev.category?.slug || categorySlug}/${prevNext.prev.slug}-${prevNext.prev.id}`}
            className="sidebar-prev-next-link group/sidebar block border-b border-gray-200 p-2.5 transition-colors sm:p-3 dark:border-slate-700"
          >
            <div className="mb-1 flex items-center gap-1">
              <svg
                className="sidebar-link-chevron h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="sidebar-link-label font-semibold uppercase tracking-wide">
                Önceki Haber
              </span>
            </div>
            <div className="flex gap-2 sm:gap-2.5">
              <div className="sidebar-thumb relative shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-100 dark:border-slate-600 dark:bg-slate-800/90">
                {getImageUrl(prevNext.prev.image, IMAGE_PRESETS.thumb) ? (
                  <Image
                    src={getImageUrl(prevNext.prev.image, IMAGE_PRESETS.thumb)}
                    alt={prevNext.prev.title}
                    fill
                    className="object-cover transition-transform duration-200 group-hover/sidebar:scale-105"
                    sizes="80px"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                    <svg className="h-4 w-4 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="line-clamp-2 font-semibold leading-snug">{prevNext.prev.title}</h4>
                <time className="mt-0.5 block text-[10px] text-gray-500 sm:text-[11px] dark:text-slate-400">{formatDate(prevNext.prev.createdAt)}</time>
              </div>
            </div>
          </Link>
        ) : null}

        {/* Current Article (Highlighted) - Always shown, 1st if no previous */}
        <div ref={currentNewsRef} className={`border-b border-gray-200 bg-red-50 p-2.5 sm:p-3 dark:border-slate-700 dark:bg-red-900/20 ${!prevNext.prev ? 'border-t border-gray-200 dark:border-slate-700' : ''}`}>
          <div className="mb-1 flex items-center gap-1">
            <div className="h-1.5 w-1.5 rounded-full bg-red-600" />
            <span className="sidebar-link-label font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">Şu An Okunan</span>
          </div>
          <div className="flex gap-2 sm:gap-2.5">
            <div className="sidebar-thumb relative shrink-0 overflow-hidden rounded bg-gray-200 dark:bg-slate-700">
              {(() => {
                const imgSrc = activeNewsId === currentNewsId && currentNewsImageUrl
                  ? currentNewsImageUrl
                  : getImageUrl(activeNews.image, IMAGE_PRESETS.thumb)
                return imgSrc ? (
                  <Image
                    src={imgSrc}
                    alt={activeNews.title}
                    fill
                    className="object-cover"
                    sizes="80px"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                    <svg className="h-4 w-4 text-gray-400 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )
              })()}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="line-clamp-2 font-semibold leading-snug text-red-600 dark:text-red-400">
                {activeNews.title}
              </h4>
              <ArticleProgressBar 
                articleSelector={activeNewsId === currentNewsId ? "#main-article" : `article[data-article-id="${activeNewsId}"]`} 
              />
            </div>
          </div>
        </div>

        {/* Sonraki: sadece ilk veri beklenirken skeleton — next=null "henüz map'te yok" anlamına da gelebilir */}
        {loading ? (
          <div className="p-2.5 sm:p-3" aria-busy="true">
            <div className="mb-2 h-3 w-20 rounded bg-gray-200 dark:bg-slate-700" />
            <div className="flex gap-2">
              <div className="sidebar-thumb shrink-0 rounded bg-gray-200 dark:bg-slate-700" />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-slate-700" />
                <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-slate-700" />
              </div>
            </div>
          </div>
        ) : prevNext.next ? (
          <Link
            href={`/${prevNext.next.category?.slug || categorySlug}/${prevNext.next.slug}-${prevNext.next.id}`}
            className="sidebar-prev-next-link group/sidebar block border-t border-gray-200 p-2.5 transition-colors sm:p-3 dark:border-slate-700"
          >
            <div className="mb-1 flex items-center gap-1">
              <span className="sidebar-link-label font-semibold uppercase tracking-wide">Sonraki Haber</span>
              <svg
                className="sidebar-link-chevron h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="flex gap-2 sm:gap-2.5">
              <div className="sidebar-thumb relative shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-100 dark:border-slate-600 dark:bg-slate-800/90">
                {getImageUrl(prevNext.next.image, IMAGE_PRESETS.thumb) ? (
                  <Image
                    src={getImageUrl(prevNext.next.image, IMAGE_PRESETS.thumb)}
                    alt={prevNext.next.title}
                    fill
                    className="object-cover transition-transform duration-200 group-hover/sidebar:scale-105"
                    sizes="80px"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800">
                    <svg className="h-4 w-4 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="line-clamp-2 font-semibold leading-snug">{prevNext.next.title}</h4>
                <time className="mt-0.5 block text-[10px] text-gray-500 sm:text-[11px] dark:text-slate-400">{formatDate(prevNext.next.createdAt)}</time>
              </div>
            </div>
          </Link>
        ) : null}

        {/* Sidebar reklam: aktif haber (Şu An Okunan) değişince yenilenir */}
        <AdSlotWrapper
          slot="sidebar"
          format="vertical"
          dense
          refreshToken={activeNewsId}
          chromeClassName="border-t border-gray-200 p-2.5 sm:p-3 dark:border-slate-700"
        />

        {marketInstruments.length > 0 ? (
          <div className="border-t border-gray-200 p-2.5 sm:p-3 dark:border-slate-700">
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400">
              Piyasalar
            </h3>
            <MarketRatesStrip
              instruments={marketInstruments}
              variant="compact"
              linked
              className="flex-col gap-2"
            />
          </div>
        ) : null}
      </div>
    </aside>
  )
}
